// Types
import Vue, { PropType } from 'vue';
import { WordCloud } from '../interfaces';

// Constants
import { LAYOUT_SET } from '../constants';

// D3
import { scalePow } from 'd3-scale';
import { extent } from 'd3-array';

export default Vue.extend({
    props: {
        width: {
            type: Number,
            required: true,
        },
        height: {
            type: Number,
            required: true,
        },
        words: {
            type: Array as PropType<WordCloud.Word[]>,
            required: true,
        },
        config: {
            type: Object as PropType<WordCloud.Config>,
            required: true,
        },
        rpx: {
            type: Number,
            required: true,
        }
    },

    data() {
        return {
            items: [] as WordCloud.Item[],
            tickCounter: 0,
            innerCenter: {
                x: 0,
                y: 0,
            },
        };
    },

    computed: {

        layoutSet(): WordCloud.ConstantSet {
            return LAYOUT_SET[this.config.layout as string || 'compact'];
        },

        innerWidth(): number {
            return (this.layoutSet.WIDTH_TRANSFORMER as (x: number) => number)(this.width);
        },

        center(): { x: number, y: number } {
            return { x: (this.width / 2), y: this.height / 2 };
        },

        origin(): { x: number, y: number } {
            return { x: 0, y: 0 };
        },

        fontSizeScale(): d3.ScalePower<number, number> {
            this.items; // force observing items
            this.config; // force observing config
            return scalePow<number, number>()
                .domain(extent<WordCloud.Item, number>(
                    this.items,
                    (d: WordCloud.Item) => d.votes
                ) as [number, number])
                .exponent(this.layoutSet.FONT_SCALE_EXPONENT)
                .range(this.config.font.sizeRange.map((d: number) => d * this.rpx));
        },

        colors(): string[] {
            return Object.values(this.config.colors);
        },
    },

    methods: {

        color(item: WordCloud.Item): string {
            return this.colors[item.text.length % this.colors.length];
        },

        fixRectangles() {
            this.items.forEach((item: WordCloud.Item) => {
                const rect = this.calculateRectBounds(item);
                if (rect) {
                    item.rectX = rect.rectX || item.rectX;
                    item.rectY = rect.rectY || item.rectY;
                    item.rectWidth = rect.rectWidth || item.rectWidth;
                    item.rectHeight = rect.rectHeight || item.rectHeight;
                }
            });

        },

        getTextBounds(
            item: Partial<WordCloud.Item>): DOMRect | undefined {
            const text = this.$el?.querySelector(`#${item.id} .smvp-word-cloud-text`) as unknown as SVGTextElement;
            return text?.getBBox?.();
        },

        calculateRectBounds(item: Partial<WordCloud.Item>): WordCloud.RectCoordinates | undefined {
            const bbox = this.getTextBounds(item);
            let rectX, rectY, rectWidth, rectHeight;

            if (bbox) {
                rectX = bbox.x - this.layoutSet.RECT_PADDING.X * this.rpx;
                rectY = bbox.y - this.layoutSet.RECT_PADDING.Y * this.rpx;
                rectWidth = this.getRectWidth(item, bbox.width);
                rectHeight = this.getRectHeight(bbox.height);
                return { rectX, rectY, rectWidth, rectHeight };
            }
        },


        getRectWidth(item: Partial<WordCloud.Item>, width?: number): number {
            return width
                ? width + this.layoutSet.RECT_PADDING.X * 2 * this.rpx
                : (item?.text?.length || 5) * this.config.font.sizeRange[1] * this.rpx;
        },

        getRectHeight(height?: number): number {
            return height
                ? height + this.layoutSet.RECT_PADDING.Y * 2 * this.rpx
                : this.config.font.sizeRange[1] * this.rpx;
        },

        externalRecenter() {
            this.innerCenter = {
                x: this.innerWidth / 2,
                y: this.height / 2,
            };
        },

        internalRecenter([topLeft, bottomRight]: [ WordCloud.Point, WordCloud.Point]) {
            this.innerCenter = {
                x: this.width - (this.width - this.innerWidth + bottomRight.x + topLeft.x) / 2,
                y: this.height - (bottomRight.y + topLeft.y) / 2,
            };
        },

    },
});
